<template>
    <div class="role-play-form-container">
        <!-- In chat the surrounding ChatAgentToolFrame is the card and supplies the padding; standalone this form is the card. -->
        <form class="flex flex-col gap-10" :class="inChat ? '' : 'bg-white p-5 rounded-xl'">
            <div class="flex flex-col">
                <div class="flex items-center justify-between gap-4">
                    <h2 class="nadia-chat-bold text-ink">{{ t("components.rolePlay.setup.title") }}</h2>
                    <div v-if="liveChatAvailable" class="w-40 shrink-0">
                        <!-- reka-ui's SelectValue captures the selected item's label text at mount and doesn't
                             re-read it on a locale change, so the closed trigger would keep showing the label in
                             whichever language was active when the option first rendered. Keying on locale remounts
                             the select so it re-reads the (already-reactive) translated option label. -->
                        <BaseSelect :key="locale" v-model="selectedMode" :options="modeOptions" trigger-class="py-3" :aria-label="t('components.rolePlay.setup.modeLabel')" />
                    </div>
                </div>
                <!-- Full-bleed: -mx-5 cancels the card's p-5 so the rule reaches both edges. -->
                <HorizontalDivider class="-mx-5 w-auto" />
                <div class="grid gap-x-5 gap-y-6 pt-2 role-play-grid">
                    <FormElement :question="t('components.rolePlay.setup.nameQuestion')">
                        <BaseInput v-model="form.name" :placeholder="t('components.rolePlay.setup.namePlaceholder')" aria-required="true" />
                    </FormElement>
                    <FormElement :question="t('components.rolePlay.setup.relationshipQuestion')">
                        <BaseInput v-model="form.relationship" :placeholder="t('components.rolePlay.setup.relationshipPlaceholder')" aria-required="true" />
                    </FormElement>
                    <FormElement :question="t('components.rolePlay.setup.personalityQuestion')" class="col-span-full">
                        <BaseInput v-model="form.personality" :placeholder="t('components.rolePlay.setup.personalityPlaceholder')" aria-required="true" />
                    </FormElement>
                    <!-- eslint-disable-next-line vuejs-accessibility/label-has-for - the VoiceTextarea's textarea is the labelled control -->
                    <label class="col-span-full flex flex-col">
                        <p class="body-small font-normal mb-3">
                            {{ t("components.rolePlay.setup.contextQuestion") }}<span class="text-ink-muted ml-1" data-testid="optional-indicator">{{ t("common.optional") }}</span>
                        </p>
                        <!-- !min-h: BaseTextarea's autosize writes an inline min-height from the content (one line
                             when empty, since field-sizing-content ignores rows), and only !important beats it. -->
                        <VoiceTextarea
                            v-model:input="form.context"
                            rows="4"
                            :append-mode="false"
                            :textarea-aria-label="t('components.rolePlay.setup.contextQuestion')"
                            textarea-class="font-normal placeholder:text-control-placeholder !min-h-[4lh]"
                            :placeholder="t('components.rolePlay.setup.contextPlaceholder')"
                            @update:busy="isDictationBusy = $event"
                            @error="recordingError = $event">
                            <!-- Errors render below the field (see siblings), so suppress the built-in error. -->
                            <template #error></template>
                        </VoiceTextarea>
                    </label>
                    <div v-if="recordingError || liveChatError" class="col-span-full">
                        <BaseNotification v-if="recordingError" theme="error" hide-dismiss class="my-2" :text="recordingError" />
                        <BaseNotification v-if="liveChatError" theme="error" :text="liveChatError" hide-dismiss class="my-2" />
                    </div>
                </div>
            </div>
            <div class="flex flex-col gap-3 md:items-end">
                <!-- The region stays mounted and the condition sits on its text, because a region
                     created together with its own message has nothing for a screen reader to notice.
                     empty:sr-only, not empty:hidden: display:none takes the element out of the
                     accessibility tree, which unregisters the region and loses the next message. -->
                <p class="body-small font-normal text-ink-muted md:text-right empty:sr-only" aria-live="polite" aria-atomic="true" data-testid="role-play-setup-missing-fields">
                    <template v-if="missingFields.length">{{ t("components.rolePlay.setup.fillIn", { fields: formattedMissingFields }) }}</template>
                </p>
                <div class="self-stretch md:self-end flex justify-center md:justify-end gap-3">
                    <BaseButton
                        v-if="inChat"
                        data-testid="role-play-setup-cancel"
                        type="button"
                        theme="neutral"
                        class="rounded-3xl flex-1 md:flex-initial"
                        @click.prevent="$emit('cancel')">
                        {{ t("common.cancel") }}
                    </BaseButton>
                    <BaseButton
                        data-testid="role-play-setup-start"
                        type="submit"
                        :disabled="!canStart"
                        theme="primary"
                        class="rounded-3xl flex-1 md:flex-initial"
                        @click.prevent="handleStart">
                        <template v-if="isFormSubmitting">
                            <div class="flex items-center">
                                <LoadingSpinner class="w-5 h-5 mr-2" />
                                <span>{{ t("components.rolePlay.setup.submitting") }}</span>
                            </div>
                        </template>
                        <template v-else>{{ t("components.rolePlay.setup.start") }}</template>
                    </BaseButton>
                </div>
            </div>
        </form>
    </div>
</template>

<script setup>
import { getCookie } from "/js/utils";
import { router } from "@inertiajs/vue3";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseNotification from "~vue/components/BaseNotification.vue";
import BaseSelect from "~vue/components/BaseSelect.vue";
import BaseInput from "~vue/components/form/BaseInput.vue";
import FormElement from "~vue/components/form/FormElement.vue";
import VoiceTextarea from "~vue/components/form/VoiceTextarea.vue";
import HorizontalDivider from "~vue/components/HorizontalDivider.vue";
import LoadingSpinner from "~vue/icons/LoadingSpinner.vue";
import { computed, inject, ref, unref, watch } from "vue";
import { useI18n } from "vue-i18n";

const { t, locale } = useI18n();

const organizationDetails = inject("organization_details");
const coachingSessionId = inject("coachingSessionId", null);

const props = defineProps({
    inChat: { type: Boolean, required: false },
    errors: {
        type: Object,
        default: () => ({}),
    },
    initialData: {
        type: Object,
        default: () => ({
            name: "",
            relationship: "",
            personality: "",
            context: "",
        }),
    },
});

const emit = defineEmits(["inChatRolePlayStarted", "cancel"]);

// The in-chat prefill can carry nulls and the standalone page passes {}, but both start endpoints
// accept a blank context only as "", so missing values start out as empty strings.
const form = ref({
    name: props.initialData.name ?? "",
    relationship: props.initialData.relationship ?? "",
    personality: props.initialData.personality ?? "",
    context: props.initialData.context ?? "",
});

// Context is optional; the rest gate the start button.
const requiredFields = computed(() => [
    { key: "name", label: t("components.rolePlay.setup.name") },
    { key: "relationship", label: t("components.rolePlay.setup.relationship") },
    { key: "personality", label: t("components.rolePlay.setup.personality") },
]);

const missingFields = computed(() => requiredFields.value.filter((field) => !form.value[field.key]?.trim()).map((field) => field.label));

const formattedMissingFields = computed(() => {
    const labels = missingFields.value;
    if (labels.length <= 1) {
        return labels.join("");
    }
    return t("components.rolePlay.setup.listAnd", { list: labels.slice(0, -1).join(t("components.rolePlay.setup.listSeparator")), last: labels[labels.length - 1] });
});

const isFormValid = computed(() => missingFields.value.length === 0);

const isFormSubmitting = ref(false);
// While recording or transcribing, form.context does not yet hold what the user said, and starting
// navigates away and unmounts the recorder before the transcript lands.
const isDictationBusy = ref(false);
const canStart = computed(() => isFormValid.value && !isFormSubmitting.value && !isDictationBusy.value);
const inChat = ref(props.inChat);

// "text" starts a text role play; "realtime" starts a live voice one. Live Chat is offered whenever the
// org enables it, with no host microphone check: probing the Teams host can hang, and a live session on a
// host without a microphone already ends with a microphone error the user can recover from by picking Text.
const selectedMode = ref("realtime");

// Dictation errors surfaced by VoiceTextarea; shown as a notification below the field.
const recordingError = ref("");
const liveChatError = ref("");

const liveChatAvailable = computed(() => Boolean(unref(organizationDetails)?.is_realtime_chat_enabled));

const modeOptions = computed(() => {
    const options = [{ key: "text", label: t("components.rolePlay.setup.textChat") }];
    if (liveChatAvailable.value) {
        options.push({ key: "realtime", label: t("components.rolePlay.setup.liveChat") });
    }
    return options;
});

// Derived rather than written back into selectedMode, so if live chat only becomes available after
// mount (the async mic check, late organization details) the Live Chat default still applies.
const effectiveMode = computed(() => (liveChatAvailable.value ? selectedMode.value : "text"));

watch(
    form,
    () => {
        liveChatError.value = "";
        recordingError.value = "";
    },
    { deep: true },
);

watch(
    () => props.errors?.roleplay_setup,
    (roleplaySetupError) => {
        if (roleplaySetupError) {
            liveChatError.value = roleplaySetupError;
            isFormSubmitting.value = false;
        }
    },
    { immediate: true },
);

/**
 * Handle starting a text-based role play
 * Emits: inChatRolePlayStarted with data object containing:
 *   - data.name: Name of the person being role-played
 *   - data.relationship: Relationship to the user
 *   - data.personality: Personality traits
 *   - data.context: Scenario/situation for the role play
 *   - chatType: "text" for text-based role play
 */
const handleStartRoleplay = () => {
    const data = {
        name: form.value.name,
        relationship: form.value.relationship,
        personality: form.value.personality,
        context: form.value.context,
    };

    isFormSubmitting.value = true;
    if (!inChat.value) {
        router.post("/role-play/setup/", data, {
            headers: {
                "X-CSRFToken": getCookie("csrftoken"),
                "Content-Type": "multipart/form-data",
            },
        });
    } else {
        emit("inChatRolePlayStarted", { data, chatType: "text" });
    }
};

/**
 * Handle starting a live (realtime voice) role play
 * Emits: inChatRolePlayStarted with data object containing:
 *   - data.name: Name of the person being role-played
 *   - data.relationship: Relationship to the user
 *   - data.personality: Personality traits
 *   - data.context: Scenario/situation for the role play
 *   - chatType: "realtime" for voice-based role play
 */
const handleStartRoleplayLiveChat = () => {
    const data = {
        name: form.value.name,
        relationship: form.value.relationship,
        personality: form.value.personality,
        context: form.value.context,
    };

    isFormSubmitting.value = true;
    liveChatError.value = "";

    // This creates the new ChatBotStream:
    // When starting from chat, include the session hashid in the URL
    const hashid = coachingSessionId;
    const url = hashid ? `/coach/realtime_chat/${hashid}/` : "/coach/realtime_chat/";
    router.post(url, data, {
        headers: {
            "X-CSRFToken": getCookie("csrftoken"),
        },
        onError: (errors) => {
            liveChatError.value = errors.roleplay_setup || t("components.rolePlay.setup.liveChatStartError");
            isFormSubmitting.value = false;
        },
        onSuccess: () => {
            // This finishes the role_play_setup action after the setup passes guardrails.
            if (inChat.value) {
                emit("inChatRolePlayStarted", { data, chatType: "realtime" });
            }
        },
    });
};

// The single start button starts the role play in whichever mode the mode select has chosen.
const handleStart = () => {
    if (!canStart.value) {
        return;
    }
    if (effectiveMode.value === "realtime") {
        handleStartRoleplayLiveChat();
    } else {
        handleStartRoleplay();
    }
};
</script>

<style scoped>
@reference "../../../css/tailwind.css";
.role-play-form-container {
    container-type: inline-size;
    container-name: role-play-form;
}

.role-play-grid {
    grid-template-columns: 1fr;
}

@container role-play-form (min-width: 600px) {
    .role-play-grid {
        grid-template-columns: repeat(2, 1fr);
    }
}

.background {
    background: radial-gradient(40% 15% at 15% 105%, rgba(255, 197, 61, 0.5) 0%, rgba(255, 197, 61, 0) 100%),
        radial-gradient(40% 15% at 50% 105%, rgba(254, 40, 144, 0.24) 0%, rgba(254, 40, 144, 0) 100%),
        radial-gradient(40% 15% at 85% 105%, rgba(169, 32, 203, 0.24) 0%, rgba(169, 32, 203, 0) 100%);
}
</style>
